import { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, FolderPlus, X, KeyRound, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { readAiProviderPref } from '../lib/aiProviderPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* iter-81 — AI Deep Research CTA.
   Sits directly below the "Tour the Transformation" hero button. Same
   pill shape, slightly larger padding to read as a peer CTA, with an
   aggressive multi-colour glow that marks it as the "magic" button.
   Click → opens the Parameter Setup dialog (DeepResearchSetupDialog)
   where the user can configure topic / lens / audience / depth AND
   optionally provide their own ChatGPT/Claude/Gemini key for THIS run.
   When the user does not enter a key, the backend falls back through:
   (1) the user's saved AI Credentials (from the AI Credentials tab)
   then (2) the shared Atrium Universal Key from the environment.
   Submit → POSTs to /api/admin/ai/deep-research → opens the resulting
   markdown in a clean reader window AND surfaces a "Save to
   Workspace" toast that opens a department/folder picker so the admin
   can persist the briefing into the workspace tree. */
export default function DeepResearchButton() {
  const [busy, setBusy] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [lastReport, setLastReport] = useState(null); // {title, report_markdown, topic, ...}
  const [saveOpen, setSaveOpen] = useState(false);
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;

  const open = useCallback(() => {
    if (busy) return;
    setSetupOpen(true);
  }, [busy]);

  const submit = useCallback(async ({ topic, focus, audience, depth, provider, apiKey }) => {
    setSetupOpen(false);
    setBusy(true);
    toast.message(`Researching: "${topic.slice(0, 60)}${topic.length > 60 ? '…' : ''}" — 30–60s for a ${depth} report.`, { duration: 8000 });

    try {
      const payload = {
        topic: topic.trim(),
        focus: (focus || '').trim() || null,
        audience: audience || 'ownership',
        depth,
      };
      // Only attach provider + per-run key when user explicitly picked
      // a non-default engine. Empty/omitted → backend uses the saved-
      // key → Atrium Universal Key fallback chain.
      if (provider && provider !== 'atrium') payload.provider = provider;
      if (apiKey && apiKey.trim()) payload.api_key = apiKey.trim();

      const { data } = await axios.post(`${API}/admin/ai/deep-research`, payload);
      // Cache the report so the Save-to-Workspace flow can use it.
      setLastReport(data);

      // Open a clean reader window with the markdown.
      const w = window.open('', '_blank');
      if (!w) {
        toast.error('Pop-up blocked — but the report is ready. Use Save to Workspace to keep it.');
      } else {
        const safe = (s) => (s || '').replace(/[<>&]/g, (m) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[m]));
        const html = `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${safe(data.title || 'Atrium AI Deep Research')}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            :root{color-scheme:light;}
            body{font-family:Manrope,system-ui,-apple-system,sans-serif;max-width:860px;margin:48px auto;padding:0 28px;color:#0A1A2F;line-height:1.65;background:#FAF8F3;}
            .badge{display:inline-block;font-size:10px;letter-spacing:.18em;text-transform:uppercase;font-weight:800;color:#fff;background:linear-gradient(135deg,#8B5CF6,#1F4FBE);padding:6px 12px;border-radius:999px;margin-bottom:14px;}
            h1{color:#003DA5;font-family:'Cormorant Garamond',Georgia,serif;font-weight:500;font-size:42px;line-height:1.1;margin:0 0 6px;border-bottom:2px solid #003DA5;padding-bottom:14px;}
            h2{color:#1F4FBE;margin-top:36px;font-size:22px;}
            h3{color:#1A2E2E;margin-top:24px;font-size:17px;}
            blockquote{border-left:4px solid #003DA5;padding:10px 16px;color:#4A5D5D;font-style:italic;background:#F0F4FA;border-radius:0 6px 6px 0;margin:18px 0;}
            table{width:100%;border-collapse:collapse;margin:18px 0;font-size:13px;}
            th,td{border:1px solid #D9E3F1;padding:8px 12px;text-align:left;}
            th{background:#F0F4FA;color:#003DA5;}
            code{background:#F0F4FA;padding:2px 6px;border-radius:4px;font-family:ui-monospace,Menlo,monospace;font-size:13px;}
            pre{white-space:pre-wrap;background:#F8F5EC;padding:20px;border-radius:10px;font-family:ui-monospace,Menlo,monospace;font-size:13px;line-height:1.6;}
            .meta{font-size:12px;color:#5C6B6B;margin-bottom:24px;}
            .meta strong{color:#003DA5;}
          </style></head><body>
          <p class="badge">✦ Atrium AI Deep Research</p>
          <div class="meta">
            Topic: <strong>${safe(data.topic || topic)}</strong>
            ${data.focus ? ` · Lens: <strong>${safe(data.focus)}</strong>` : ''}
            · Depth: <strong>${safe(data.depth || depth)}</strong>
            ${typeof data.word_count === 'number' ? ` · ~${data.word_count.toLocaleString()} words` : ''}
            ${data.confidence_summary ? `<br>Confidence: <strong>${safe(data.confidence_summary)}</strong>` : ''}
          </div>
          <pre>${safe(data.report_markdown || '')}</pre>
          </body></html>`;
        w.document.write(html);
        w.document.close();
      }

      // Offer "Save to Workspace" via a sticky sonner toast (admin only —
      // viewers can read but not persist). 30s window so the admin has
      // time to scan the report before deciding to file it.
      if (adminToken) {
        toast.success('Deep research ready.', {
          duration: 30000,
          action: {
            label: 'Save to Workspace',
            onClick: () => setSaveOpen(true),
          },
        });
      } else {
        toast.success('Deep research opened in a new tab.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deep research failed. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }, [adminToken]);

  return (
    <>
      <button
        type="button"
        data-testid="hero-deep-research-cta"
        onClick={open}
        disabled={busy}
        className="deep-research-glow inline-flex items-center gap-2 px-8 py-3 rounded-full text-white font-semibold text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-80 disabled:cursor-progress"
        style={{
          fontFamily: 'Manrope, sans-serif',
          background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 50%, #1F4FBE 100%)',
          border: '1px solid rgba(255,255,255,0.18)',
        }}
        title="Let the AI run a deep research sweep on any topic — hospitality, Caribbean tourism, design, finance, anything."
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Researching…
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            AI Deep Research
          </>
        )}
      </button>

      {/* Parameter Setup screen. Opens on click; collects topic, lens,
          audience, depth, provider, and an optional per-run API key. */}
      <DeepResearchSetupDialog
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onSubmit={submit}
      />

      {/* Save-to-Workspace picker. Renders here so it can read the
          `lastReport` state set during the run. Admin-only by virtue of
          the toast that opens it, but we also gate the actual save
          call on adminToken to be safe. */}
      <SaveResearchDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        report={lastReport}
      />
    </>
  );
}

/* iter-80 — Save AI Deep Research to a Workspace folder.
   Loads the full workspace tree, lets the admin pick any documents-
   capable node, then:
     1) wraps the markdown report in a Blob,
     2) POSTs to /api/admin/upload to get a persistent file URL,
     3) POSTs to /api/admin/workspace/documents creating an entry
        on the chosen node.
   The result is a regular workspace document that ALL the standard
   tooling (view, archive, snapshot, restore) already supports — no
   special-casing needed downstream. */
function SaveResearchDialog({ open, onClose, report }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [tree, setTree] = useState([]);
  const [parentId, setParentId] = useState('');
  const [childId, setChildId] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [saving, setSaving] = useState(false);

  // Load tree on first open.
  useEffect(() => {
    if (!open || !adminToken) return;
    axios.get(`${API}/workspace/tree`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(({ data }) => setTree(Array.isArray(data) ? data : []))
      .catch(() => setTree([]));
  }, [open, adminToken]);

  // Default the title to the report's own title when the dialog opens.
  useEffect(() => {
    if (open && report?.title && !customTitle) {
      setCustomTitle(report.title);
    }
  }, [open, report, customTitle]);

  const roots = useMemo(() => tree.filter((n) => !n.parent_id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [tree]);
  const children = useMemo(
    () => tree.filter((n) => n.parent_id === parentId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [tree, parentId],
  );

  // Either the picked child OR the root itself (when the root accepts
  // documents directly — e.g. "Brand Standards") is the target.
  const targetNode = useMemo(() => {
    if (childId) return tree.find((n) => n.id === childId) || null;
    if (parentId) return tree.find((n) => n.id === parentId) || null;
    return null;
  }, [tree, parentId, childId]);

  const canSave = !!(report?.report_markdown && targetNode && customTitle.trim() && adminToken && !saving);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // 1) Upload the markdown as a .md file. Using the same upload
      //    endpoint the rest of the app uses keeps the storage path,
      //    object-storage mirroring, and ranged playback consistent.
      const filename = `${(customTitle || 'ai-deep-research').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60)}.md`;
      const blob = new Blob([report.report_markdown], { type: 'text/markdown' });
      const fd = new FormData();
      fd.append('file', new File([blob], filename, { type: 'text/markdown' }));
      const upload = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
      });
      const fileUrl = upload?.data?.url;
      if (!fileUrl) throw new Error('Upload failed — no URL returned');

      // 2) Create the workspace document entry on the chosen node.
      await axios.post(
        `${API}/admin/workspace/documents`,
        {
          node_id: targetNode.id,
          title: customTitle.trim(),
          description: report.topic
            ? `AI Deep Research · ${report.topic}${report.focus ? ` · ${report.focus}` : ''}${report.depth ? ` · ${report.depth} depth` : ''}`
            : 'AI Deep Research briefing',
          file_url: fileUrl,
          file_type: 'md',
          file_name: filename,
        },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );

      toast.success(`Saved to ${targetNode.label}.`);
      onClose?.();
      setParentId(''); setChildId(''); setCustomTitle('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to workspace.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md w-[95vw] p-0 overflow-hidden" data-testid="save-research-dialog">
        <DialogTitle className="sr-only">Save research to workspace</DialogTitle>
        <DialogDescription className="sr-only">
          Pick a workspace department and folder to file this AI research briefing.
        </DialogDescription>
        <div className="px-5 py-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #1F4FBE 100%)', fontFamily: 'Manrope' }}>
          <FolderPlus className="w-5 h-5 text-white" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#C7D2FE' }}>Atrium AI</p>
            <p className="text-sm font-bold text-white">Save research to Workspace</p>
          </div>
          <button onClick={onClose} type="button" aria-label="Close" className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" data-testid="save-research-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4" style={{ fontFamily: 'Manrope' }}>
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>Department</label>
            <select
              value={parentId}
              onChange={(e) => { setParentId(e.target.value); setChildId(''); }}
              data-testid="save-research-parent"
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
              style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
            >
              <option value="">— Select a department —</option>
              {roots.map((n) => (
                <option key={n.id} value={n.id}>{n.label}</option>
              ))}
            </select>
          </div>

          {parentId && children.length > 0 && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>Folder (optional)</label>
              <select
                value={childId}
                onChange={(e) => setChildId(e.target.value)}
                data-testid="save-research-child"
                className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
                style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
              >
                <option value="">— Department root —</option>
                {children.map((n) => (
                  <option key={n.id} value={n.id}>{n.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>Document title</label>
            <input
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              data-testid="save-research-title"
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
              placeholder="e.g. NPR benchmarks — Caribbean inclusive 2026"
              style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
            />
          </div>

          {targetNode && (
            <div className="px-3 py-2 rounded-lg text-[11px]" style={{ backgroundColor: '#F0F4FA', color: '#1F4FBE' }}>
              Will be saved to <strong>{targetNode.label}</strong> as a markdown document.
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-xs font-bold"
              style={{ backgroundColor: '#E5E7EB', color: '#374151' }}
              data-testid="save-research-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              data-testid="save-research-submit"
              className="px-4 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 inline-flex items-center gap-1.5"
              style={{ background: 'linear-gradient(135deg, #6366F1 0%, #1F4FBE 100%)' }}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />}
              {saving ? 'Saving…' : 'Save to Workspace'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* iter-81 — AI Deep Research · Parameter Setup screen.
   Replaces three sequential window.prompt() dialogs with a single
   structured form where the user configures:
     • Topic            (required)
     • Lens / Focus     (optional)
     • Audience         (ownership / operations / marketing / engineering / general)
     • Depth            (quick · standard · deep)
     • Provider         (Atrium AI · OpenAI · Claude · Gemini)
     • Per-run API key  (optional — shown only for non-Atrium providers)

   Credential resolution priority (mirrors backend get_api_key()):
     1. The key typed on THIS screen (per-run override)
     2. The key saved on the user's profile via the AI Credentials tab
     3. The shared Atrium Universal Key from the environment

   A live "Using" indicator at the bottom tells the user exactly which
   credential source will be used before they submit, so there's no
   guesswork about cost / quota ownership. */
const PROVIDERS = [
  { id: 'atrium',    label: 'Atrium AI',     model: 'gpt-5.2 (Atrium-managed)', color: '#1F4FBE', requiresKey: false, prefix: '' },
  { id: 'openai',    label: 'ChatGPT (OpenAI)', model: 'gpt-5.2 via your OpenAI account', color: '#10A37F', requiresKey: true,  prefix: 'sk-' },
  { id: 'anthropic', label: 'Claude (Anthropic)', model: 'claude-sonnet-4.5 via your Anthropic account', color: '#D97757', requiresKey: true, prefix: 'sk-ant-' },
  { id: 'gemini',    label: 'Gemini (Google)',   model: 'gemini-3-pro via your Google AI Studio account', color: '#4285F4', requiresKey: true, prefix: 'AIza' },
];

const AUDIENCES = [
  { id: 'ownership',  label: 'Ownership / Executive',  hint: 'CFO/CEO tone, strategic framing, ROI-led.' },
  { id: 'operations', label: 'Operations / GM',        hint: 'Day-to-day execution focus, departmental playbook lens.' },
  { id: 'marketing',  label: 'Brand & Marketing',      hint: 'Audience-led, narrative-led, market positioning.' },
  { id: 'engineering',label: 'Design & Engineering',   hint: 'Technical depth, materials, code/standards, vendor angle.' },
  { id: 'general',    label: 'General / Mixed',        hint: 'Balanced overview suitable for all stakeholders.' },
];

const DEPTHS = [
  { id: 'quick',    label: 'Quick',    words: '~600–900 words',   findings: '3–4 key findings',   eta: '~15–25s' },
  { id: 'standard', label: 'Standard', words: '~1.2k–1.8k words', findings: '5–7 key findings',   eta: '~30–45s' },
  { id: 'deep',     label: 'Deep',     words: '~2k–3k words',     findings: '7–10 findings + data tables', eta: '~45–75s' },
];

function DeepResearchSetupDialog({ open, onClose, onSubmit }) {
  const [topic, setTopic] = useState('');
  const [focus, setFocus] = useState('');
  const [audience, setAudience] = useState('ownership');
  const [depth, setDepth] = useState('deep');
  const [provider, setProvider] = useState('atrium');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [savedAiKeys, setSavedAiKeys] = useState({}); // {openai: true, anthropic: false, gemini: true}

  // Reset on close so the next open is a clean form. Keep the
  // chosen provider — most users stick with one engine for a session.
  useEffect(() => {
    if (!open) return;
    setTopic('');
    setFocus('');
    setApiKey('');
    setShowKey(false);
    // Seed the provider from the global aiProviderPicker preference, if any.
    const pref = readAiProviderPref?.();
    if (pref?.provider && PROVIDERS.find((p) => p.id === pref.provider)) {
      setProvider(pref.provider);
    }
    // Fetch the user's saved keys (admin only — viewer JWT also OK for /auth/me).
    const t = localStorage.getItem('admin_token') || localStorage.getItem('atrium_token');
    if (t) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
        .then(({ data }) => {
          const k = (data && data.ai_keys) || {};
          setSavedAiKeys({
            openai: !!(k.openai && k.openai.length > 4),
            anthropic: !!(k.anthropic && k.anthropic.length > 4),
            gemini: !!(k.gemini && k.gemini.length > 4),
          });
        })
        .catch(() => setSavedAiKeys({}));
    }
  }, [open]);

  const selectedProvider = useMemo(() => PROVIDERS.find((p) => p.id === provider) || PROVIDERS[0], [provider]);

  // Credential-source indicator. Mirrors the backend resolution order so
  // the user sees the exact same precedence the server will apply.
  const credentialSource = useMemo(() => {
    if (provider === 'atrium') {
      return { kind: 'atrium', label: 'Atrium in-app credentials (Universal Key)', tone: 'info' };
    }
    const trimmed = (apiKey || '').trim();
    if (trimmed) {
      return { kind: 'inline', label: `Your ${selectedProvider.label} key (this run only)`, tone: 'success' };
    }
    if (savedAiKeys[provider]) {
      return { kind: 'saved', label: `Saved ${selectedProvider.label} key (from AI Credentials tab)`, tone: 'success' };
    }
    return { kind: 'fallback', label: 'Atrium in-app credentials (fallback — no key provided)', tone: 'info' };
  }, [provider, apiKey, savedAiKeys, selectedProvider]);

  const keyLooksValid = useMemo(() => {
    if (!selectedProvider.requiresKey) return true;
    const t = (apiKey || '').trim();
    if (!t) return true; // empty is OK — will fall back
    return t.startsWith(selectedProvider.prefix.slice(0, 3));
  }, [selectedProvider, apiKey]);

  const canSubmit = !!(topic.trim().length >= 4 && keyLooksValid);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    onSubmit?.({
      topic: topic.trim(),
      focus: focus.trim(),
      audience,
      depth,
      provider,
      apiKey: apiKey.trim(),
    });
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-xl w-[95vw] p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="deep-research-setup-dialog">
        <DialogTitle className="sr-only">AI Deep Research — Parameter Setup</DialogTitle>
        <DialogDescription className="sr-only">
          Configure the topic, audience, depth, and AI provider for your deep research briefing.
        </DialogDescription>

        <div className="px-5 py-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg,#8B5CF6 0%,#6366F1 50%,#1F4FBE 100%)', fontFamily: 'Manrope' }}>
          <Sparkles className="w-5 h-5 text-white" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#C7D2FE' }}>Atrium AI</p>
            <p className="text-sm font-bold text-white">AI Deep Research · Parameter Setup</p>
          </div>
          <button onClick={onClose} type="button" aria-label="Close" className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center" data-testid="deep-research-setup-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4" style={{ fontFamily: 'Manrope' }}>
          {/* Topic */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>
              Topic <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              data-testid="deep-research-topic"
              autoFocus
              rows={2}
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
              style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
              placeholder='e.g. "All-inclusive vs European Plan ADR uplift in the Caribbean 2024–2026"'
            />
            <p className="text-[10px] mt-1" style={{ color: '#7A8C8C' }}>
              Be specific — date ranges, geography, and metrics produce sharper briefings.
            </p>
          </div>

          {/* Lens / Focus */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>
              Lens / Focus (optional)
            </label>
            <input
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              data-testid="deep-research-focus"
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
              style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
              placeholder='e.g. "hospitality CFO", "post-hurricane reopening", "Caribbean tourism market"'
            />
          </div>

          {/* Audience */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>
              Audience
            </label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              data-testid="deep-research-audience"
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
              style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
            >
              {AUDIENCES.map((a) => (
                <option key={a.id} value={a.id}>{a.label} — {a.hint}</option>
              ))}
            </select>
          </div>

          {/* Depth */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>
              Depth
            </label>
            <div className="grid grid-cols-3 gap-2">
              {DEPTHS.map((d) => {
                const active = depth === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setDepth(d.id)}
                    data-testid={`deep-research-depth-${d.id}`}
                    className="px-3 py-2 rounded-lg text-left text-[11px] border transition-all"
                    style={{
                      borderColor: active ? '#1F4FBE' : '#D9E3F1',
                      background: active ? '#F0F4FA' : 'white',
                      color: active ? '#003DA5' : '#4A5D5D',
                    }}
                  >
                    <div className="font-bold text-xs" style={{ color: active ? '#003DA5' : '#1A2E2E' }}>{d.label}</div>
                    <div className="text-[10px] mt-0.5">{d.words}</div>
                    <div className="text-[10px]">{d.findings}</div>
                    <div className="text-[10px] mt-1 italic" style={{ opacity: 0.8 }}>{d.eta}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Provider */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color: '#7A8C8C' }}>
              AI Provider
            </label>
            <select
              value={provider}
              onChange={(e) => { setProvider(e.target.value); setApiKey(''); setShowKey(false); }}
              data-testid="deep-research-provider"
              className="w-full px-3 py-2 rounded-lg border text-sm bg-white"
              style={{ borderColor: '#D9E3F1', color: '#1A2E2E' }}
            >
              {PROVIDERS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label} — {p.model}
                </option>
              ))}
            </select>
          </div>

          {/* Per-run API key — shown only for non-Atrium providers. */}
          {selectedProvider.requiresKey && (
            <div className="rounded-lg p-3" style={{ background: '#F8F5EC', border: `1px solid ${selectedProvider.color}33` }}>
              <label className="block text-[10px] uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1.5" style={{ color: selectedProvider.color }}>
                <KeyRound className="w-3 h-3" />
                Your {selectedProvider.label} API key (optional — this run only)
              </label>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  data-testid="deep-research-api-key"
                  className="w-full pl-3 pr-10 py-2 rounded-lg border text-sm bg-white font-mono"
                  style={{ borderColor: keyLooksValid ? '#D9E3F1' : '#DC2626', color: '#1A2E2E' }}
                  placeholder={`${selectedProvider.prefix}…`}
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  aria-label={showKey ? 'Hide key' : 'Show key'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[#7A8C8C] hover:text-[#1A2E2E]"
                  data-testid="deep-research-toggle-key"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!keyLooksValid && (
                <p className="text-[10px] mt-1.5" style={{ color: '#DC2626' }}>
                  This doesn't look like a {selectedProvider.label} key (expected prefix <code>{selectedProvider.prefix}</code>). Submit anyway or fix the value.
                </p>
              )}
              <p className="text-[10px] mt-1.5" style={{ color: '#7A8C8C' }}>
                Leave blank to fall back to {savedAiKeys[provider] ? (
                  <>your <strong>saved {selectedProvider.label} key</strong> from the AI Credentials tab.</>
                ) : (
                  <>the shared <strong>Atrium in-app credentials</strong> (Universal Key).</>
                )}
                {' '}This key is sent securely and is <strong>not saved</strong> unless you add it via the AI Credentials tab.
              </p>
            </div>
          )}

          {/* Credential-source indicator — mirrors the backend resolution order. */}
          <div
            className="rounded-lg px-3 py-2 flex items-start gap-2"
            data-testid="deep-research-credential-indicator"
            data-source={credentialSource.kind}
            style={{
              background: credentialSource.tone === 'success' ? '#ECFDF5' : '#F0F4FA',
              border: `1px solid ${credentialSource.tone === 'success' ? '#10B98133' : '#1F4FBE33'}`,
            }}
          >
            <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: credentialSource.tone === 'success' ? '#059669' : '#1F4FBE' }} />
            <div className="text-[11px]" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
              <strong style={{ color: credentialSource.tone === 'success' ? '#059669' : '#003DA5' }}>Using:</strong> {credentialSource.label}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="deep-research-setup-cancel"
              className="px-4 py-2 rounded-lg text-xs font-bold"
              style={{ backgroundColor: '#E5E7EB', color: '#374151' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="deep-research-setup-submit"
              className="px-5 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 inline-flex items-center gap-1.5"
              style={{ background: 'linear-gradient(135deg,#8B5CF6 0%,#6366F1 50%,#1F4FBE 100%)' }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Run deep research
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

